/**
 * Análisis biométrico en cliente: microgestos + expresiones con ventana móvil.
 * Normaliza por tamaño del rostro para que los valores cambien entre sesiones y estados.
 */

const FRAME_WINDOW = 24;
const BASELINE_FRAMES = 8;

const ROLE_FEELING_LABEL = {
    mal: 'Carga visible',
    regular: 'Activación moderada',
    bien: 'Estable',
};

function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
}

function mean(arr) {
    if (!arr.length) return 0;
    return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function eyeAspectRatio(eye) {
    const v1 = Math.hypot(eye[1].x - eye[5].x, eye[1].y - eye[5].y);
    const v2 = Math.hypot(eye[2].x - eye[4].x, eye[2].y - eye[4].y);
    const h = Math.hypot(eye[0].x - eye[3].x, eye[0].y - eye[3].y);
    if (h < 1e-6) return 0.25;
    return (v1 + v2) / (2 * h);
}

function interOcularDistance(landmarks) {
    const le = landmarks.getLeftEye();
    const re = landmarks.getRightEye();
    const lx = mean(le.map((p) => p.x));
    const ly = mean(le.map((p) => p.y));
    const rx = mean(re.map((p) => p.x));
    const ry = mean(re.map((p) => p.y));
    return Math.hypot(rx - lx, ry - ly) || 1;
}

function pushSample(arr, value, max = FRAME_WINDOW) {
    arr.push(value);
    while (arr.length > max) arr.shift();
}

export function createBiometricSession() {
    return {
        earSamples: [],
        tensionSamples: [],
        mouthSamples: [],
        browSamples: [],
        expressionFrames: [],
        blinkEvents: [],
        lastBlink: false,
        startedAt: Date.now(),
    };
}

function averageExpressions(frames) {
    const keys = ['neutral', 'happy', 'sad', 'angry', 'fearful', 'disgusted', 'surprised'];
    const out = {};
    if (!frames.length) {
        keys.forEach((k) => {
            out[k] = 0;
        });
        return out;
    }
    keys.forEach((k) => {
        out[k] = mean(frames.map((f) => f[k] || 0));
    });
    return out;
}

function topExpression(avg) {
    const sorted = Object.entries(avg).sort((a, b) => b[1] - a[1]);
    return { name: sorted[0][0], score: sorted[0][1] };
}

/**
 * @param {*} detections face-api detection with landmarks + expressions
 * @param {ReturnType<createBiometricSession>} session
 */
export function analyzeFaceFrame(detections, session) {
    const landmarks = detections.landmarks;
    const expressions = { ...detections.expressions };
    const iod = interOcularDistance(landmarks);

    const leftEye = landmarks.getLeftEye();
    const rightEye = landmarks.getRightEye();
    const ear = (eyeAspectRatio(leftEye) + eyeAspectRatio(rightEye)) / 2;

    const isBlinking = ear < 0.2;
    if (isBlinking && !session.lastBlink) {
        session.blinkEvents.push(Date.now());
        const now = Date.now();
        session.blinkEvents = session.blinkEvents.filter((t) => now - t < 45000);
    }
    session.lastBlink = isBlinking;

    const brow = {
        x: (landmarks.positions[21].x + landmarks.positions[22].x) / 2,
        y: (landmarks.positions[21].y + landmarks.positions[22].y) / 2,
    };
    const jaw = landmarks.positions[8];
    const upperLip = landmarks.positions[51];
    const lowerLip = landmarks.positions[57];
    const innerBrowL = landmarks.positions[21];
    const innerBrowR = landmarks.positions[22];

    const tensionNorm = clamp(Math.hypot(brow.x - jaw.x, brow.y - jaw.y) / iod, 0.35, 1.35);
    const mouthOpenNorm = clamp(Math.hypot(upperLip.x - lowerLip.x, upperLip.y - lowerLip.y) / iod, 0, 0.45);
    const browFurrowNorm = clamp(Math.hypot(innerBrowL.x - innerBrowR.x, innerBrowL.y - innerBrowR.y) / iod, 0.08, 0.35);

    pushSample(session.earSamples, ear);
    pushSample(session.tensionSamples, tensionNorm);
    pushSample(session.mouthSamples, mouthOpenNorm);
    pushSample(session.browSamples, browFurrowNorm);
    pushSample(session.expressionFrames, expressions);

    const avgExpr = averageExpressions(session.expressionFrames);
    const dominant = topExpression(avgExpr);

    const earNow = mean(session.earSamples.slice(-6));
    const earBase = mean(session.earSamples.slice(0, BASELINE_FRAMES)) || earNow;
    const earDelta = earNow - earBase;

    const tensionNow = mean(session.tensionSamples.slice(-6));
    const tensionBase = mean(session.tensionSamples.slice(0, BASELINE_FRAMES)) || tensionNow;
    const tensionDelta = tensionNow - tensionBase;

    const browNow = mean(session.browSamples.slice(-6));
    const mouthNow = mean(session.mouthSamples.slice(-6));

    const stressExpr =
        (avgExpr.sad || 0) * 1.1 +
        (avgExpr.angry || 0) * 1.15 +
        (avgExpr.fearful || 0) * 1.05 +
        (avgExpr.disgusted || 0) * 0.9;
    const activationExpr = (avgExpr.surprised || 0) * 1.1 + (avgExpr.happy || 0) * 0.35;
    const calmExpr = (avgExpr.neutral || 0) * 0.85 + (avgExpr.happy || 0) * 0.55;

    const microStress =
        clamp(tensionDelta * 2.2, 0, 1) * 0.35 +
        clamp(-earDelta * 8, 0, 1) * 0.25 +
        clamp(browNow - 0.14, 0, 1) * 0.2 +
        clamp(session.blinkEvents.length / 12, 0, 1) * 0.2;

    const stressScore = clamp(stressExpr * 0.55 + microStress * 0.45 - calmExpr * 0.25, 0, 1);
    const activationScore = clamp(activationExpr + microStress * 0.3 - calmExpr * 0.15, 0, 1);

    let feeling = 'bien';
    if (stressScore >= 0.42 || (dominant.name === 'sad' && dominant.score > 0.28)) {
        feeling = 'mal';
    } else if (activationScore >= 0.38 || stressScore >= 0.28 || dominant.name === 'surprised') {
        feeling = 'regular';
    }

    const bpm = Math.round((session.blinkEvents.length / Math.max(1, (Date.now() - session.startedAt) / 60000)) * 10) / 10;

    const microNotes = [];
    if (earDelta < -0.025) microNotes.push('parpadeo o párpado más bajo que al inicio');
    if (tensionDelta > 0.06) microNotes.push('mandíbula y ceño más tensos');
    if (mouthNow > 0.22) microNotes.push('boca más abierta (sorpresa o alivio)');
    if (browNow > 0.2) microNotes.push('ceño fruncido');
    if (session.blinkEvents.length >= 4) microNotes.push('parpadeo frecuente');

    const feedbackText = buildFeedbackText({
        feeling,
        dominant,
        avgExpr,
        microNotes,
        stressScore,
        activationScore,
    });

    return {
        bpm,
        ear: earNow.toFixed(3),
        tension: Math.round(tensionNow * 100),
        mouthOpen: Math.round(mouthNow * 100),
        browFurrow: Math.round(browNow * 100),
        feeling,
        feelingLabel: ROLE_FEELING_LABEL[feeling],
        stressScore: Math.round(stressScore * 100),
        activationScore: Math.round(activationScore * 100),
        dominantExpression: dominant.name,
        dominantScore: Math.round(dominant.score * 100),
        microNotes,
        feedbackText,
        rawExpressions: avgExpr,
        isConsistent: true,
        timestamp: Date.now(),
    };
}

function buildFeedbackText({ feeling, dominant, avgExpr, microNotes, stressScore, activationScore }) {
    const exprEs = {
        neutral: 'neutral',
        happy: 'calidez',
        sad: 'pesar',
        angry: 'tensión',
        fearful: 'alerta',
        disgusted: 'rechazo',
        surprised: 'sorpresa',
    };
    const domLabel = exprEs[dominant.name] || dominant.name;
    const micro = microNotes.length ? ` Microgestos: ${microNotes.join('; ')}.` : '';
    const pct = Math.round(dominant.score * 100);

    if (feeling === 'mal') {
        return `Lectura: carga emocional (${domLabel} ~${pct}%, estrés ${stressScore}%).${micro} No es diagnóstico: invita a pausa y calibración.`;
    }
    if (feeling === 'regular') {
        return `Lectura: activación moderada (${domLabel} ~${pct}%, activación ${activationScore}%).${micro} Conviene un solo foco antes de seguir.`;
    }
    return `Lectura: bastante estable (${domLabel} ~${pct}%).${micro} Puedes contrastar con cómo te sientes por escrito.`;
}
